import React, { useEffect } from "react";

import { getObjectByPath, } from "../../../utils";
import PropTypes from 'prop-types';
import { variableCurrencyValue } from "../../../components/field-table";
import { RegionSelectGrouped } from "../../../components/MaterialSelects/RegionSelectGrouped";
import { SundryTypeSelect } from "../../../components/MaterialSelects/SundryTypeSelect";
import { Tooltip } from "@mui/material";
import { FieldTable } from "../../../components/FieldTable/FieldTable";
import { useOutletContext } from "react-router-dom";
import { CampaignOutletContextValue } from "../form";
import { Sundry } from "../../../generated/graphql";
import { FieldTableColumnProps } from "../../../components/FieldTable/FieldTableTypes";

const newRow = () => ({
    campaignComponent: {
        unit_value_local: 0,
        qty: 0,
        currency: null
    },
    region: null,
    sundryType: null,
    use_global_discount: false,
    name: ''
});

const summaries = {
    footer: (old, newRow) => {
        return !newRow;
    }
}


const SundriesPane = () => {
    const {
        values,
        setValue,
        loading,
        readOnly,
        errors,
        setTab,
        isReadOnly
    } = useOutletContext<CampaignOutletContextValue>();


    const columns: FieldTableColumnProps<Sundry>[] = [
        {
            header: 'Type',
            accessor: 'sundryType',
            dataType: 'select',
            options: {
                label: 'name',
                renderEditor: ({ field }) => {
                    return <SundryTypeSelect {...field} autoFocus={true} />;
                }
            }
        },
        {
            header: 'Cost Region',
            accessor: 'campaignComponent.region',
            sort: 'campaignComponent.region.name',
            dataType: 'select',
            options: {
                renderViewer: ({ field, row }) => {
                    const rt = getObjectByPath(row, 'campaignComponent.regionType');
                    return rt?.name ? rt.name + ' / ' + field?.value?.name : '';
                },
                renderEditor: function rtEdit({ field }) {
                    const changeRt = (evt) => {
                        const name = evt.target.name;
                        field.onChange({ target: { name: name, value: evt?.target?.value } });
                        field.onChange({ target: { name: name + 'Type', value: evt?.target?.value?.regionType } });
                    }
                    return <RegionSelectGrouped isCompositeChange={true} {...field} allowBlank={true} onChange={changeRt}
                        autoFocus={true} />;
                }
            }
        }, {
            header: 'Name',
            accessor: 'name',
            dataType: 'text',
        }, {
            header: 'Date Up',
            accessor: 'date_up',
            dataType: 'date',
        },
        {
            header: 'Date Down',
            accessor: 'date_down',
            dataType: 'date',
        }, {
            header: 'Unit Cost',
            accessor: 'campaignComponent.unit_value_local',
            dataType: 'currency',
            readOnly: () => {
                return isReadOnly(true)
            },
            options: {
                currency: {
                    accessor: 'campaignComponent.currency',
                    allowContextCurrency: true
                }
            },
            summaries: {
                // eslint-disable-next-line react/prop-types, react/display-name
                footer: () => {
                    return <b style={{ display: 'flex', alignItems: 'center' }}>Total</b>
                }
            }
        }, {
            header: 'Qty',
            accessor: 'campaignComponent.qty',
            dataType: 'number',
            readOnly: () => {
                return isReadOnly(true)
            },
            // eslint-disable-next-line react/prop-types, react/display-name
            summaries: {
                footer: ({ rows }) => {
                    return rows.reduce((total, current) => {
                        return total + (parseInt(current.campaignComponent?.qty) || 0);
                    }, 0)
                }
            }
        }, {
            header: 'Total',
            accessor: 'total',
            dataType: 'currency',
            readOnly: true,
            options: {
                getValue: ({ row }) => {
                    return row.campaignComponent?.qty * row.campaignComponent?.unit_value_local;
                }
                ,
                currency: {
                    accessor: 'campaignComponent.currency'
                }
                /*renderViewer: ({currency, row}) => {
                  return variableCurrencyValue({
                      currency: currency,
                      value: row.campaignComponent?.qty * row.campaignComponent?.unit_value_local,
                      places: 2
                  });
              },*/
            },
            summaries: {
                footer: ({ rows, currency }) => {
                    // if multiple currencies, do NOT sum here...
                    const defaultCurrency = currency?.id;
                    let seenOther = false;
                    const value = rows.reduce((total, current) => {
                        const cid = current.campaignComponent.currency?.id;
                        if (cid && defaultCurrency != cid) {
                            seenOther = true;
                        }
                        return (total + (parseInt(current.campaignComponent?.qty) * parseInt(current.campaignComponent?.unit_value_local)));
                    }, 0);
                    if (seenOther) return null;
                    return variableCurrencyValue({ currency: currency, value: value });
                }
            }
        }, {
            header: 'Total Quote',
            accessor: 'total_quote',
            className: 'column-currency',
            dataType: 'currency',
            readOnly: true,
            hideWhen: ({ exchangeRateContext }) => {
                if (exchangeRateContext.options && exchangeRateContext.options.length > 1) return false;
                return true;
            },
            options: {
                getValue: ({ row, calcExchangeRate }) => {
                    if (!row.campaignComponent) return '';
                    return calcExchangeRate(row.campaignComponent?.currency, row.campaignComponent?.unit_value_local, row.campaignComponent?.qty);
                }
            },
            summaries: {
                footer: ({ rows, currency, calcExchangeRate }) => {
                    const value = rows.reduce((total, current) => {
                        return (total + calcExchangeRate(current.campaignComponent?.currency, (parseInt(current.campaignComponent?.qty) * parseInt(current.campaignComponent?.unit_value_local))));
                    }, 0);
                    return variableCurrencyValue({ currency: currency, value: value });
                }
            }
        }, {
            header: <Tooltip
                title="Apply any % campaign discount to this row?"><span>Campaign Discount</span></Tooltip>,
            accessor: 'use_global_discount',
            dataType: 'boolean'
        }

    ];


    useEffect(() => {
        setTab('sundries');
    }, []);
    const allowAddDelete = !(readOnly || isReadOnly(true));
    return <FieldTable
        columns={columns}
        name="sundries"
        newRow={newRow}
        setValue={setValue}
        values={values.sundries}
        newRowLabel={allowAddDelete ? "Add entry" : null}
        summaries={summaries}
        hasDelete={allowAddDelete}
        loading={loading}
        hasExport={false}
        errors={errors}
        readOnly={readOnly}
        className="campaigns__sundries is-scrollable"
    />;

}

SundriesPane.propTypes = {
    values: PropTypes.array,
    setValue: PropTypes.func
};

export default SundriesPane;
